import { Dialog } from "$components/dialog/dialog";
import { DialogBody as UnstyledDialogBody } from "$components/dialog/dialog-body";
import { DialogFooter } from "$components/dialog/dialog-footer";
import RHFTextField from "$components/rhf/text-field";
import { DatePicker as RHFDatePicker } from "$components/rhf-fields";
import { toast } from "$global-providers/toast-provider";
import { gql, useApolloClient } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { styled } from "@material-ui/core";
import { format } from "date-fns";
import { type PaymentOrigin, PaymentStatus } from "elephant-web/gql";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";
import { useRecordPaymentInPatientHeaderMutation } from "./__generated__/registration-payment-dialog.gql";

gql`
  mutation RecordPaymentInPatientHeader($input: RecordPaymentInput!) {
    recordPayment(input: $input) {
      id
    }
  }
`;

const DialogBody = styled(UnstyledDialogBody)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "baseline",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

const DatePicker = styled(RHFDatePicker)({
  "& .MuiInputBase-root": {
    flex: 1,
    minWidth: 200,
  },
});

const ReceiptNumberField = styled(RHFTextField)({
  flex: 1,
  minWidth: 200,
});

const formSchema = yup.object({
  paymentDate: yup.date().required(),
  receiptNumber: yup.string(),
});
type FormValues = yup.Asserts<typeof formSchema>;

export const RegistrationPaymentDialog: FC<{
  open: boolean;
  onClose: () => void;
  patientId: string;
  origin?: PaymentOrigin;
}> = ({ open, onClose, patientId, origin = undefined }) => {
  const apolloClient = useApolloClient();
  const { formatMessage } = useIntl();
  const [savePayment, { loading: submitLoading }] =
    useRecordPaymentInPatientHeaderMutation({
      onCompleted: () => {
        apolloClient.cache.modify({
          id: `Patient:${patientId}`,
          fields: {
            registrationPayment: () => {
              return { status: PaymentStatus.Paid };
            },
          },
        });
        onClose();
      },
      onError: (error) => {
        toast.error(
          formatMessage(
            {
              id: "patientDetails.paymentDialog.erro",
              defaultMessage: "There was an error: {error}",
            },
            { error: error.message },
          ),
        );
      },
    });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(formSchema),
    mode: "onTouched",
    defaultValues: {
      paymentDate: new Date(),
    },
  });

  return (
    <Dialog open={open} onCancel={onClose} title="Take Registration Payment">
      <FormProvider {...formMethods}>
        <form
          onSubmit={formMethods.handleSubmit(
            ({ paymentDate, receiptNumber }) => {
              savePayment({
                variables: {
                  input: {
                    paidOn: format(paymentDate, "yyyy-MM-dd"),
                    patientId,
                    receiptNumber,
                    origin,
                  },
                },
              });
            },
          )}
        >
          <DialogBody>
            <DatePicker
              name="paymentDate"
              label={formatMessage({
                id: "patientDetails.paymentDialog.date",
                defaultMessage: "Payment Date",
              })}
              hideHelper
            />
            <ReceiptNumberField
              name="receiptNumber"
              label={formatMessage({
                id: "patientDetails.paymentDialog.receiptNumber",
                defaultMessage: "Receipt number",
              })}
              hideHelper
            />
          </DialogBody>
          <DialogFooter
            submitLoading={submitLoading}
            submitLabel={formatMessage({
              id: "patientDetails.paymentDialog.submit",
              defaultMessage: "Submit",
            })}
          />
        </form>
      </FormProvider>
    </Dialog>
  );
};
